import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    VStack,
    Grid,
    Select,
    useTheme,
    Flex,
    Text,
} from '@chakra-ui/react';
import { MdClear } from 'react-icons/md';
import { FiSearch } from 'react-icons/fi';
import dayjs from 'dayjs';
import api from '../../../auth/apiClient';
import type { HarvestMonitoringFilters } from '../types';

interface Company {
    id: number;
    name: string;
}

interface Estate {
    id: number;
    name: string;
    odoo_id: number;
    company_id?: number;
}

interface Division {
    id: number;
    name: string;
    estate_id: number;
    odoo_id: number;
}

interface FilterHarvestMapProps {
    onFiltersChange: (filters: HarvestMonitoringFilters) => void;
    isLoading?: boolean;
}

export const FilterHarvestMap: React.FC<FilterHarvestMapProps> = ({
    onFiltersChange,
    isLoading = false,
}) => {
    const theme = useTheme();
    const today = dayjs().format('YYYY-MM-DD');
    const [currentDate, setCurrentDate] = useState<string>(today);

    // State for Select options
    const [companies, setCompanies] = useState<Company[]>([]);
    const [estates, setEstates] = useState<Estate[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [allDivisions, setAllDivisions] = useState<Division[]>([]);

    const [selectedCompanyId, setSelectedCompanyId] = useState<number | undefined>(undefined);
    const [selectedEstateId, setSelectedEstateId] = useState<number | undefined>(undefined); // Default to all estates
    const [selectedDivisionId, setSelectedDivisionId] = useState<number | undefined>(undefined);

    const filteredEstates = useMemo(() => {
        if (!selectedCompanyId) return estates;
        return estates.filter((estate) => estate.company_id === selectedCompanyId);
    }, [estates, selectedCompanyId]);

    // Style constants
    const inputStyles = {
        size: 'sm' as const,
        borderRadius: 'lg',
        borderColor: 'gray.200',
        _hover: { borderColor: 'gray.300' },
        _focus: {
            borderColor: theme.colors.blue[400],
            boxShadow: `0 0 0 1px ${theme.colors.blue[400]}`,
        },
    };

    // Fetch options on component mount
    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [optionsResponse, estatesDbResponse] = await Promise.all([
                    api.get('/inspeksi/options'),
                    api.get('/inspeksi/web/estate-db'),
                ]);

                const data = optionsResponse.data;
                const estatesDbData = estatesDbResponse.data;

                const fetchedCompanies = data.companies || [];
                setCompanies(fetchedCompanies);
                setAllDivisions(data.divisis || []);

                const mergedEstates: Estate[] = (estatesDbData || []).map((estateDb: any) => {
                    const optionsEstate = (data.estates || []).find((est: any) => est.name === estateDb.name);
                    return {
                        id: estateDb.id,
                        name: estateDb.name,
                        company_id: estateDb.company_id,
                        odoo_id: optionsEstate?.odoo_id || estateDb.id,
                    };
                });

                setEstates(mergedEstates);

                if (fetchedCompanies.length > 0) {
                    const defaultCompany = fetchedCompanies[0];
                    setSelectedCompanyId(defaultCompany.id);

                    const formattedDate = dayjs(today).format('YYYYMMDD');
                    
                    // Default to all estates within default company
                    const companyEstateIds = new Set(mergedEstates.filter(e => e.company_id === defaultCompany.id).map(e => e.id));
                    const allowedDivs = (data.divisis || []).filter((d: any) => companyEstateIds.has(d.estate_id)).map((d: any) => d.name);

                    onFiltersChange({
                        current_date: formattedDate,
                        company_id: defaultCompany.id,
                        estate_id: undefined, // Default all estates
                        allowed_divisions: allowedDivs
                    });
                }
            } catch (error) {
                console.error('Error fetching options:', error);
            }
        };
        fetchOptions();
    }, []);

    // Filter divisions when estate changes
    useEffect(() => {
        if (!selectedEstateId) {
            setDivisions([]);
            setSelectedDivisionId(undefined);
            return;
        }

        const selectedEstate = estates.find(e => e.odoo_id === selectedEstateId);
        if (selectedEstate) {
            const filteredDivisions = allDivisions.filter(
                (div: Division) => div.estate_id === selectedEstate.id
            );
            setDivisions(filteredDivisions);
        }
    }, [selectedEstateId, estates, allDivisions]);

    const handleApplyFilters = () => {
        const formattedDate = dayjs(currentDate).format('YYYYMMDD');
        let allowedDivs: string[] | undefined = undefined;

        if (selectedDivisionId) {
            const division = divisions.find(d => d.odoo_id === selectedDivisionId);
            if (division) allowedDivs = [division.name];
        } else if (selectedEstateId) {
            const estate = estates.find(e => e.odoo_id === selectedEstateId);
            if (estate) {
                allowedDivs = allDivisions.filter(d => d.estate_id === estate.id).map(d => d.name);
            }
        } else if (selectedCompanyId) {
            const companyEstateIds = new Set(estates.filter(e => e.company_id === selectedCompanyId).map(e => e.id));
            allowedDivs = allDivisions.filter(d => companyEstateIds.has(d.estate_id)).map(d => d.name);
        }

        onFiltersChange({
            current_date: formattedDate,
            company_id: selectedCompanyId,
            estate_id: selectedEstateId,
            division_id: selectedDivisionId,
            allowed_divisions: allowedDivs
        });
    };

    const handleReset = () => {
        setCurrentDate(today);
        setSelectedEstateId(undefined);
        setSelectedDivisionId(undefined);

        let resetCompanyId: number | undefined = undefined;
        let allowedDivs: string[] | undefined = undefined;

        if (companies.length > 0) {
            resetCompanyId = companies[0].id;
            setSelectedCompanyId(resetCompanyId);

            const companyEstateIds = new Set(estates.filter(e => e.company_id === resetCompanyId).map(e => e.id));
            allowedDivs = allDivisions.filter(d => companyEstateIds.has(d.estate_id)).map(d => d.name);
        }

        const formattedDate = dayjs(today).format('YYYYMMDD');
        onFiltersChange({
            current_date: formattedDate,
            company_id: resetCompanyId,
            estate_id: undefined,
            allowed_divisions: allowedDivs
        });
    };

    return (
        <Box
            bg="white"
            width="100%"
        >
            <VStack spacing={4} align="stretch">
                <Grid templateColumns="1fr" gap={4}>
                    {/* Tanggal */}
                    <Box width="full">
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">Tanggal</Text>
                        <Input
                            type="date"
                            value={currentDate}
                            onChange={(e) => setCurrentDate(e.target.value)}
                            {...inputStyles}
                            width="full"
                        />
                    </Box>

                    {/* PT / Company */}
                    <Box width="full">
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">PT / Company</Text>
                        <Select
                            placeholder="Pilih PT"
                            value={selectedCompanyId || ''}
                            onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : undefined;
                                setSelectedCompanyId(val);
                                setSelectedEstateId(undefined);
                                setSelectedDivisionId(undefined);
                            }}
                            {...inputStyles}
                            width="full"
                        >
                            {companies.map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Select>
                    </Box>

                    {/* Estate */}
                    <Box width="full">
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">Estate</Text>
                        <Select
                            placeholder="Semua Estate"
                            value={selectedEstateId || ''}
                            onChange={(e) => {
                                const val = e.target.value ? Number(e.target.value) : undefined;
                                setSelectedEstateId(val);
                                setSelectedDivisionId(undefined);
                            }}
                            {...inputStyles}
                            width="full"
                        >
                            {filteredEstates.map((e) => (
                                <option key={e.odoo_id} value={e.odoo_id}>{e.name}</option>
                            ))}
                        </Select>
                    </Box>

                    {/* Divisi */}
                    <Box width="full">
                        <Text fontSize="sm" fontWeight="500" mb={2} color="gray.600">Divisi</Text>
                        <Select
                            placeholder="Semua Divisi"
                            value={selectedDivisionId || ''}
                            onChange={(e) => setSelectedDivisionId(e.target.value ? Number(e.target.value) : undefined)}
                            {...inputStyles}
                            disabled={!selectedEstateId}
                            width="full"
                        >
                            {divisions.map((d) => (
                                <option key={d.odoo_id} value={d.odoo_id}>{d.name}</option>
                            ))}
                        </Select>
                    </Box>
                </Grid>

                <Flex justify="flex-end" gap={2}>
                    <Button
                        size="sm"
                        leftIcon={<MdClear />}
                        variant="outline"
                        colorScheme="gray"
                        onClick={handleReset}
                        borderRadius="lg"
                    >
                        Hapus Filter
                    </Button>
                    <Button
                        size="sm"
                        leftIcon={<FiSearch />}
                        colorScheme="green"
                        onClick={handleApplyFilters}
                        isLoading={isLoading}
                        borderRadius="lg"
                    >
                        Terapkan Filter
                    </Button>
                </Flex>
            </VStack>
        </Box>
    );
};
